import { useEffect } from 'react';
import { validateAuthToken } from '@/utils/auth/tokenAuthUtils';
import { getAuthToken } from '@/utils/auth/tokenAuthUtils';
import { logger } from "@/utils/logger";

/**
 * Hook to maintain PWA session on app resume
 * Prevents session loss after inactivity by auto-renewing JWT
 */
export const usePwaSessionKeepAlive = () => {
    useEffect(() => {
        let lastResumeTime = Date.now();

        const handleVisibilityChange = async () => {
            // Only act when app comes to FOREGROUND
            if (document.visibilityState === 'visible') {
                const now = Date.now();
                const timeSinceLastResume = now - lastResumeTime;
                lastResumeTime = now;

                // If app was in background for more than 5 minutes, refresh session
                if (timeSinceLastResume > 5 * 60 * 1000) {
                    logger.info('🔄 App resumed after', Math.round(timeSinceLastResume / 1000 / 60), 'minutes');
                    logger.info('🔄 Refreshing session to prevent timeout...');

                    try {
                        const token = getAuthToken();

                        if (token) {
                            // Validate token - this also renews the Supabase JWT session
                            const { valid, revoked, error } = await validateAuthToken(token);

                            if (valid && !revoked) {
                                logger.info('✅ Session renewed successfully');
                            } else {
                                logger.info('⚠️ Token validation failed:', error || 'revoked');
                                // Don't force logout here - let ProtectedRoute handle it
                            }
                        } else {
                            logger.info('ℹ️ No token found - user not logged in');
                        }
                    } catch (error) {
                        logger.error('❌ Error refreshing session on resume:', error);
                        // Don't force logout on error - network might be temporarily down
                    }
                }
            }
        };

        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Also listen for focus (belt and suspenders)
        window.addEventListener('focus', handleVisibilityChange);

        // Cleanup
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleVisibilityChange);
        };
    }, []);
};
